import React from 'react';
import { API_BASE } from '../services/api';

interface EstateSummary {
    id: string;
    deceased_name: string | null;
    date_of_death: string | null;
    status: 'draft' | 'unlocked';
    created_at: string;
    updated_at: string;
    current_step: number;
    question_count: number;
}

interface Props {
    onOpenEstate: (id: string) => void;
    onNewCase: () => void;
}

function fmtDate(iso: string | null): string {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return '';
    }
}

function fmtUpdated(iso: string): string {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffM = Math.floor((now.getTime() - d.getTime()) / (1000 * 60));
        if (diffM < 1) return 'just now';
        if (diffM < 60) return `${diffM}m ago`;
        const diffH = Math.floor(diffM / 60);
        if (diffH < 24) return `${diffH}h ago`;
        return fmtDate(iso);
    } catch {
        return '';
    }
}

export const YourReportsBanner: React.FC<Props> = ({ onOpenEstate, onNewCase }) => {
    const [estates, setEstates] = React.useState<EstateSummary[] | null>(null);
    const [error, setError] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/estates`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then((data: EstateSummary[]) => {
                if (!cancelled) setEstates(data);
            })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, []);

    if (estates === null && !error) return null;
    if (error) return null;

    // Consumer model: a user has at most one saved audit. If legacy data
    // contains multiple estate rows, surface only the most recently
    // updated one — older rows stay in the database but are hidden from
    // the consumer homepage. Unlocked > drafts so a paid report is always
    // preferred over a stray older draft.
    const list = estates ?? [];
    const sorted = [...list].sort((a, b) => {
        if (a.status !== b.status) return a.status === 'unlocked' ? -1 : 1;
        return b.updated_at.localeCompare(a.updated_at);
    });
    const primary = sorted[0] ?? null;

    return (
        <section>
            <div className="mb-5">
                <div className="text-[10px] tracking-eyebrow uppercase text-ink3 font-semibold mb-1">
                    Your saved audit
                </div>
                <div className="text-[12px] text-ink3">
                    {primary
                        ? primary.status === 'unlocked'
                            ? 'Your full report is ready to view.'
                            : 'Pick up where you left off.'
                        : "You haven't started an audit yet."}
                </div>
            </div>

            {!primary ? (
                <p className="font-serif italic text-[14px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent max-w-[520px]">
                    Begin your probate review when you're ready — it takes a few minutes
                    and saves as you go.
                </p>
            ) : (
                <ul className="border-t border-rule">
                    {(() => {
                        const estate = primary;
                        const isUnlocked = estate.status === 'unlocked';
                        return (
                            <li key={estate.id} className="border-b border-rule">
                                <button
                                    onClick={() => onOpenEstate(estate.id)}
                                    className="group w-full text-left py-4 flex items-baseline gap-6 hover:bg-paper transition-colors px-2"
                                >
                                    <span className="font-serif italic text-[12px] text-accent shrink-0 w-12">
                                        {isUnlocked ? 'Final' : 'Draft'}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block font-serif text-[16px] text-ink truncate">
                                            {estate.deceased_name || 'Unnamed estate'}
                                        </span>
                                        {estate.date_of_death && (
                                            <span className="block text-[11px] text-ink3 mt-0.5">
                                                Date of death — {fmtDate(estate.date_of_death)}
                                            </span>
                                        )}
                                    </span>
                                    <span className="font-mono text-[10px] tracking-[0.06em] text-ink3 hidden sm:block">
                                        {fmtUpdated(estate.updated_at)}
                                    </span>
                                    <span className="text-[12px] text-ink3 group-hover:text-accent transition-colors shrink-0 font-serif italic">
                                        →
                                    </span>
                                </button>
                            </li>
                        );
                    })()}
                </ul>
            )}
        </section>
    );
};
